import axios from '@raas/shared/src/utils/axios';
import type { AxiosRequestConfig } from '@raas/shared/src/utils/axios';
import { IUser, IPendingUser } from '@raas/shared/src/types/user';
import { IAuthenticatedUser } from '@raas/shared/src/types/authenticated-user';

class UsersService {
  async login(login: string, password: string): Promise<IAuthenticatedUser> {
    const response = await axios.post<IAuthenticatedUser>('/users/login', {
      login,
      password,
    });

    return response.data;
  }

  async createOrLogin(accessToken: string): Promise<IAuthenticatedUser> {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    const response = await axios.post<IAuthenticatedUser>('/users/create-or-login');
    delete axios.defaults.headers.common.Authorization;
    return response.data;
  }

  async changePassword(email: string, password: string, newPassword: string): Promise<void> {
    await axios.post<void>('/users/change-password', {
      login: email,
      password,
      newPassword,
    });
  }

  async getProfile(): Promise<IUser> {
    const response = await axios.get<IUser>('/users/profile');
    return response.data;
  }

  async getAll(abortSignal?: AbortSignal): Promise<IUser[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const response = await axios.get<IUser[]>('/users', config);
    return response.data;
  }

  async getUsersByGroup(groupId: string, abortSignal?: AbortSignal): Promise<IUser[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const response = await axios.get<IUser[]>(`/users/group/${groupId}`, config);
    return response.data;
  }

  async signUp(accessToken: string): Promise<boolean> {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    const response = await axios.post<boolean>('/users/signup');
    delete axios.defaults.headers.common.Authorization;
    return response.data;
  }

  async getPendingUsers(abortSignal?: AbortSignal): Promise<IPendingUser[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const response = await axios.get<IPendingUser[]>('/users/pending', config);
    return response.data;
  }

  async approvePendingUser(id: string): Promise<void> {
    await axios.post<void>(`/users/pending/${id}/approve`);
  }

  async declinePendingUser(id: string): Promise<void> {
    await axios.post<void>(`/users/pending/${id}/decline`);
  }
}

const instance = new UsersService();
export default instance;
